import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

/**
 * RadarChart - Comparação multi-dimensional de jogadores
 * 
 * @param {Array} players - Array de objetos com dados normalizados 0-100
 * @param {Array} dimensions - Array de strings com nomes das métricas
 * 
 * Exemplo:
 * players = [
 *   { player_name: 'Messi', goals: 95, assists: 88, passing: 92, ... },
 *   { player_name: 'Haaland', goals: 98, assists: 65, passing: 70, ... }
 * ]
 * dimensions = ['goals', 'assists', 'passing', 'duels', 'tackles', 'creativity']
 */
export default function RadarChart({ players = [], dimensions = [] }) {
  if (!players || players.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <p>Nenhum dado disponível para visualização</p>
      </div>
    );
  }

  // Mapear dimensões para labels mais amigáveis
  const dimensionLabels = {
    goals: 'Gols',
    assists: 'Assistências',
    passing: 'Passes',
    duels: 'Duelos',
    tackles: 'Desarmes',
    creativity: 'Criatividade',
    shooting: 'Finalização',
    defense: 'Defesa',
    pace: 'Velocidade',
    physical: 'Físico'
  };

  const labels = dimensions.map(dim => dimensionLabels[dim] || dim.toUpperCase());

  // Cores para até 5 jogadores
  const playerColors = [
    { bg: 'rgba(6, 78, 59, 0.2)', border: BRAND_GREEN }, // Verde principal
    { bg: 'rgba(180, 140, 44, 0.2)', border: BRAND_GOLD }, // Dourado
    { bg: 'rgba(59, 130, 246, 0.2)', border: '#3b82f6' }, // Azul
    { bg: 'rgba(239, 68, 68, 0.2)', border: '#ef4444' }, // Vermelho
    { bg: 'rgba(168, 85, 247, 0.2)', border: '#a855f7' }, // Roxo
  ];

  const datasets = players.slice(0, 5).map((player, idx) => {
    const colors = playerColors[idx] || playerColors[0];
    
    return {
      label: player.player_name || `Jogador ${idx + 1}`,
      data: dimensions.map(dim => player[dim] || 0),
      backgroundColor: colors.bg,
      borderColor: colors.border,
      borderWidth: 2,
      pointBackgroundColor: colors.border,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: colors.border,
      pointHoverRadius: 6,
    };
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20,
          color: '#6b7280',
          font: {
            size: 11,
            weight: '500'
          },
          backdropColor: 'transparent',
          callback: function(value) {
            return value;
          }
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.15)',
          lineWidth: 1
        },
        angleLines: {
          color: 'rgba(107, 114, 128, 0.15)',
          lineWidth: 1
        },
        pointLabels: {
          color: '#374151',
          font: {
            size: 12,
            weight: '600'
          },
          padding: 10
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#1f2937',
          font: {
            size: 13,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: BRAND_GOLD,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = Math.round(context.parsed.r);
            return `${label}: ${value}/100`;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      intersect: false
    }
  };

  return (
    <div style={{ height: '400px', position: 'relative', width: '100%' }}>
      <Radar data={{ labels, datasets }} options={options} />
    </div>
  );
}
